import { trim } from 'lodash';

import type { AssetType } from 'src/data/asset';
import { GENDER_TONE } from 'src/data/demographics';
import { PROJECT_TYPE, type ProjectType } from 'src/data/project/const';
import { hashString } from 'src/lib/string';

import { type AiSuggestionMode, DEFAULT_LANGUAGE, type Language } from './const';
import { isRecomposeGenerationResult, isSuggestGenerationResult } from './generationResult';
import { alignLanguage } from './language';
import type { GenerationRequest, RecomposeRequest, SuggestionRequest } from './types';

interface CommonParams {
  briefTitle?: string;
  briefBody?: string;
  keywords?: string;
  language: Language;
  toneOfVoice?: string;
}

interface CanvasParams extends CommonParams {
  assetType: AssetType;
}

interface SuggestParams extends CommonParams {
  suggestionMode: AiSuggestionMode;
  inputText?: string;
  suggestionAnnotationType?: string;
  suggestionAnnotationContent?: string;
  suggestionAnnotationMinAge?: number;
  suggestionAnnotationMaxAge?: number;
  link?: string;
}

interface RecomposeParams extends CommonParams {
  recomposeMode?: string;
  inputTexts?: string[];
  genderTone?: string;
  agePreset?: string;
}

const LINE_DELIMITER = '\n';
const VALUE_DELIMITER = ',';

const DEFAULT_RECOMPOSE_PARAMS = {
  genderTone: GENDER_TONE.neutral,
};

const prepareStringValue = (str?: string): string => trim(str || '');
const prepareArrayValue = (arr?: string[]): string => {
  const aligned = arr || [];
  return prepareStringValue(aligned.join(VALUE_DELIMITER));
};

const getCommonParamsFromRequest = (request: GenerationRequest): CommonParams => ({
  briefTitle: request.briefTitle,
  briefBody: request.briefBody,
  keywords: request.keywords,
  language: (request.language && alignLanguage(request.language)) || DEFAULT_LANGUAGE,
  toneOfVoice: request.tovId,
});

const getCanvasParamsFromRequest = (request: GenerationRequest): CanvasParams => ({
  ...getCommonParamsFromRequest(request),
  assetType: request.assetType,
});
const serializeCanvasParams = (params: CanvasParams): string => {
  const list = [
    `briefTitle=${prepareStringValue(params.briefTitle)}`,
    `briefBody=${prepareStringValue(params.briefBody)}`,
    `keywords=${prepareStringValue(params.keywords)}`,
    `language=${prepareStringValue(params.language)}`,
    `assetType=${prepareStringValue(params.assetType)}`,
  ];
  return list.join(LINE_DELIMITER);
};

const getSuggestParamsFromRequest = (request: SuggestionRequest): SuggestParams => ({
  ...getCommonParamsFromRequest(request),
  suggestionMode: request.suggestionMode,
  inputText: request.inputText,
  suggestionAnnotationType: request.suggestionAnnotationType,
  suggestionAnnotationContent: request.suggestionAnnotationContent,
  suggestionAnnotationMinAge: request.suggestionAnnotationMinAge,
  suggestionAnnotationMaxAge: request.suggestionAnnotationMaxAge,
  link: request.link,
});
const serializeSuggestParams = (params: SuggestParams): string => {
  const list = [
    `suggestionMode=${prepareStringValue(params.suggestionMode)}`,
    `inputText=${prepareStringValue(params.inputText)}`,
    `toneOfVoice=${prepareStringValue(params.toneOfVoice)}`,
    `suggestionAnnotationType=${prepareStringValue(params.suggestionAnnotationType)}`,
    `suggestionAnnotationContent=${prepareStringValue(params.suggestionAnnotationContent)}`,
    `suggestionAnnotationMinAge=${prepareStringValue(String(params.suggestionAnnotationMinAge))}`,
    `suggestionAnnotationMaxAge=${prepareStringValue(String(params.suggestionAnnotationMaxAge))}`,
    `link=${prepareStringValue(params.link)}`,
    `keywords=${prepareStringValue(params.keywords)}`,
    `language=${prepareStringValue(params.language)}`,
  ];
  return list.join(LINE_DELIMITER);
};

const getRecomposeParamsFromRequest = (request: RecomposeRequest): RecomposeParams => ({
  ...getCommonParamsFromRequest(request),
  recomposeMode: request.recomposeMode,
  inputTexts: request.inputTexts,
  genderTone: request.genderTone,
  agePreset: request.agePreset,
});
const serializeRecomposeParams = (params: RecomposeParams): string => {
  const alignedParams = {
    ...DEFAULT_RECOMPOSE_PARAMS,
    ...params,
  };
  const list = [
    `inputTexts=${prepareArrayValue(alignedParams.inputTexts)}`,
    `recomposeMode=${prepareStringValue(alignedParams.recomposeMode)}`,
    `toneOfVoice=${prepareStringValue(alignedParams.toneOfVoice)}`,
    `genderTone=${prepareStringValue(alignedParams.genderTone)}`,
    `agePreset=${prepareStringValue(alignedParams.agePreset)}`,
    `language=${prepareStringValue(alignedParams.language)}`,
  ];
  return list.join(LINE_DELIMITER);
};

const serializeRequestParams = (projectType: ProjectType, request: GenerationRequest): string => {
  if (projectType === PROJECT_TYPE.canvas) {
    const params = getCanvasParamsFromRequest(request);
    return serializeCanvasParams(params);
  }

  if (isSuggestGenerationResult(request)) {
    const params = getSuggestParamsFromRequest(request);
    return serializeSuggestParams(params);
  }

  if (isRecomposeGenerationResult(request)) {
    const params = getRecomposeParamsFromRequest(request);
    return serializeRecomposeParams(params);
  }

  return '';
};

export const hashGenerationRequestParams = (
  projectType: ProjectType,
  request: GenerationRequest
): number => {
  const serialized = serializeRequestParams(projectType, request);
  return hashString(serialized);
};

export const hashCanvasGenerationParams = (params: CanvasParams): number => {
  const serialized = serializeCanvasParams(params);
  return hashString(serialized);
};

export const hashSuggestGenerationParams = (params: SuggestParams): number => {
  const serialized = serializeSuggestParams(params);
  return hashString(serialized);
};

export const hashRecomposeGenerationParams = (params: RecomposeParams): number => {
  const serialized = serializeRecomposeParams(params);
  return hashString(serialized);
};
